/**
 * Typed product barcodes (APP-06, Rhys 2026-10-29: "choose EAN as default
 * barcode type but you can add more or change the type and then that will
 * flow through to packing validation etc").
 *
 * Shopify Admin API 2026-10 replaced ProductVariant.barcode with
 * `barcodes(first:) { nodes { type value } }`: up to 20 per variant, 255
 * chars each, the FIRST one is what the deprecated `barcode` still returns.
 * Sending `barcodes` replaces the whole set; typed values are validated by
 * Shopify (length / prefix / check digit), untyped (type null = "Other") are
 * stored as-is.
 *
 * Product cache docs keep `barcode` (= first, back-compat for every reader)
 * and gain `barcodes: [{ value, type }]` plus `barcodeKeys` (match keys for
 * Firestore array-contains-any lookups). Docs written before this have only
 * `barcode`; productBarcodeList() falls back to it, so every reader tolerates
 * both shapes.
 *
 * Mirror of backend/utils/productBarcodes.js (keep the two in step).
 */
import { cleanBarcodeForSave as cleanBarcode } from './barcodePlaceholders';

export const BARCODE_TYPES = ['EAN', 'UPC', 'GTIN', 'ISBN', 'ASIN'];
// NS_PID exists in Shopify's enum; never offered in the UI, preserved if found.
const KNOWN_BARCODE_TYPES = new Set([...BARCODE_TYPES, 'NS_PID']);
export const MAX_BARCODES = 20;
const MAX_BARCODE_LENGTH = 255;

export function normalizeBarcodeType(type) {
  const text = String(type || '').trim().toUpperCase();
  return KNOWN_BARCODE_TYPES.has(text) ? text : null;
}

export function normalizeBarcodeValue(value) {
  return cleanBarcode(String(value ?? '').replace(/[\r\n\t]/g, '').trim());
}

/** GS1 mod-10 check digit (EAN-8/13, UPC-A, GTIN-14, ISBN-13). */
export function gs1CheckDigitValid(digits) {
  if (!/^\d{8,14}$/.test(digits)) return false;
  let sum = 0;
  const body = digits.slice(0, -1);
  for (let i = 0; i < body.length; i += 1) {
    // Weights alternate 3,1 counting from the digit next to the check digit.
    const weight = (body.length - i) % 2 === 1 ? 3 : 1;
    sum += Number(body[i]) * weight;
  }
  return (10 - (sum % 10)) % 10 === Number(digits[digits.length - 1]);
}

export function isbn10Valid(text) {
  if (!/^\d{9}[\dX]$/.test(text)) return false;
  let sum = 0;
  for (let i = 0; i < 10; i += 1) {
    const ch = text[i];
    sum += (ch === 'X' ? 10 : Number(ch)) * (10 - i);
  }
  return sum % 11 === 0;
}

const compact = (value) => String(value || '').replace(/[\s-]/g, '').toUpperCase();

/** Does `value` satisfy `type`'s standard? type null = Other = always true. */
export function barcodeFitsType(value, type) {
  const text = compact(value);
  switch (normalizeBarcodeType(type)) {
    case null: return Boolean(text);
    case 'EAN': return /^(\d{8}|\d{13})$/.test(text) && gs1CheckDigitValid(text);
    case 'UPC': return /^\d{12}$/.test(text) && gs1CheckDigitValid(text);
    case 'GTIN': return /^(\d{8}|\d{12}|\d{13}|\d{14})$/.test(text) && gs1CheckDigitValid(text);
    case 'ISBN': return (/^97[89]\d{10}$/.test(text) && gs1CheckDigitValid(text)) || isbn10Valid(text);
    case 'ASIN': return /^[A-Z0-9]{10}$/.test(text);
    default: return true; // NS_PID: no published rule, Shopify decides
  }
}

/**
 * The type a value most likely is, or null (Other) when it fits none.
 * Order matters: a valid 13-digit is an EAN (ISBN-13s are EANs too), a valid
 * 12-digit is a UPC, 8 is EAN-8, 14 is GTIN-14.
 */
export function suggestBarcodeType(value) {
  const text = compact(value);
  if (!text) return null;
  if (/^\d+$/.test(text)) {
    if ((text.length === 13 || text.length === 8) && gs1CheckDigitValid(text)) return 'EAN';
    if (text.length === 12 && gs1CheckDigitValid(text)) return 'UPC';
    if (text.length === 14 && gs1CheckDigitValid(text)) return 'GTIN';
    if (text.length === 10 && isbn10Valid(text)) return 'ISBN';
    return null;
  }
  if (isbn10Valid(text)) return 'ISBN';
  if (/^B0[A-Z0-9]{8}$/.test(text)) return 'ASIN';
  return null;
}

export const typeLabel = (type) => normalizeBarcodeType(type) || 'Other';

/**
 * One barcode against its type. { valid, message, suggestedType } where
 * suggestedType is the type that DOES fit (null = Other) when it is invalid.
 */
export function validateBarcode(value, type) {
  const text = normalizeBarcodeValue(value);
  if (!text) return { valid: false, message: 'Enter a barcode.', suggestedType: null };
  if (text.length > MAX_BARCODE_LENGTH) return { valid: false, message: `Barcodes are at most ${MAX_BARCODE_LENGTH} characters.`, suggestedType: null };
  const wanted = normalizeBarcodeType(type);
  if (barcodeFitsType(text, wanted)) return { valid: true, message: '', suggestedType: wanted };
  const suggestedType = suggestBarcodeType(text);
  const hint = suggestedType ? `It looks like a ${suggestedType}.` : 'Save it as Other.';
  return { valid: false, message: `${text} is not a valid ${wanted}. ${hint}`, suggestedType };
}

/**
 * GTIN-tolerant key for an all-digit barcode (8-14 digits): leading zeros
 * dropped, so UPC-A 760625796930, EAN-13 0760625796930 and GTIN-14
 * 00760625796930 are one item. Anything else: trimmed and upper-cased.
 * Same rule as services/ship/scan.js gtinKey (Ship validation since 2026-10).
 */
export function barcodeMatchKey(value) {
  const text = String(value || '').trim().toUpperCase();
  if (!text) return '';
  if (/^\d{8,14}$/.test(text)) return text.replace(/^0+/, '') || '0';
  return text;
}

/** Every Firestore key a barcode answers to (exact upper-case + GTIN key). */
export function barcodeLookupKeys(value) {
  const text = String(value || '').trim().toUpperCase();
  if (!text) return [];
  return [...new Set([text, barcodeMatchKey(text)])];
}

/**
 * Clean list: trimmed, placeholder-free, known types only, de-duplicated on
 * the match key (first wins), capped at 20. Accepts strings or { value, type }.
 */
export function normalizeBarcodeList(list) {
  const out = [];
  const seen = new Set();
  for (const entry of Array.isArray(list) ? list : []) {
    const raw = entry && typeof entry === 'object' ? entry.value : entry;
    const value = normalizeBarcodeValue(raw);
    if (!value) continue;
    const key = barcodeMatchKey(value);
    if (seen.has(key)) continue;
    seen.add(key);
    const type = normalizeBarcodeType(entry && typeof entry === 'object' ? entry.type : null);
    // Typed values are written without spaces / hyphens (ISBN 978-0-...),
    // the form Shopify validates; Other stays exactly as entered.
    out.push({ value: type && type !== 'NS_PID' ? compact(value) : value, type });
    if (out.length >= MAX_BARCODES) break;
  }
  return out;
}

/** A product doc's barcodes, falling back to the single legacy `barcode`. */
export function productBarcodeList(doc) {
  if (!doc || typeof doc !== 'object') return [];
  if (Array.isArray(doc.barcodes) && doc.barcodes.length) return normalizeBarcodeList(doc.barcodes);
  return normalizeBarcodeList([doc.barcode]);
}

/** Just the values (for scan matchers and order lines). */
export function productBarcodeValues(doc) {
  return productBarcodeList(doc).map((entry) => entry.value);
}

/** Firestore `barcodeKeys` for a list (array-contains-any lookups). */
export function barcodeKeysFor(list) {
  return [...new Set(normalizeBarcodeList(list).flatMap((entry) => barcodeLookupKeys(entry.value)))];
}

/** Does `scan` match any of these barcode values (GTIN tolerant)? */
export function barcodesMatchScan(values, scan) {
  const want = barcodeMatchKey(scan);
  if (!want) return false;
  return (Array.isArray(values) ? values : [values]).some((value) => {
    const raw = value && typeof value === 'object' ? value.value : value;
    return barcodeMatchKey(raw) === want;
  });
}

/** Shopify GraphQL `barcodes(first: N) { nodes { type value } }` → clean list. */
export function barcodesFromGraphql(node) {
  const raw = node?.barcodes;
  const nodes = Array.isArray(raw) ? raw : (raw?.nodes || raw?.edges?.map((edge) => edge?.node) || []);
  // Older payloads (or a version without the connection) carry only `barcode`.
  return nodes.length ? normalizeBarcodeList(nodes) : normalizeBarcodeList([node?.barcode]);
}

/**
 * Cache fields for a variant: `barcode` (first, legacy), `barcodes`, `barcodeKeys`.
 * `fallbackBarcode` covers payloads that only carry the deprecated field.
 */
export function barcodeCacheFields(list, fallbackBarcode = null) {
  const barcodes = Array.isArray(list) && list.length ? normalizeBarcodeList(list) : normalizeBarcodeList([fallbackBarcode]);
  return {
    barcode: barcodes[0]?.value || null,
    barcodes,
    barcodeKeys: barcodeKeysFor(barcodes)
  };
}

/**
 * Server-side check of a list about to be written to Shopify. Returns
 * { ok, barcodes, errors: [{ index, value, message, suggestedType }] }.
 * `trusted`: entries Shopify already holds (same value + type) skip the type
 * check, so a code Shopify accepted under a rule we don't model (UPC-E, say)
 * never blocks an unrelated edit.
 */
export function validateBarcodeList(list, trusted = []) {
  const trustedKeys = new Set(normalizeBarcodeList(trusted).map((entry) => `${entry.value}|${entry.type || ''}`));
  if (!Array.isArray(list)) return { ok: false, barcodes: [], errors: [{ index: -1, value: '', message: 'barcodes must be a list.', suggestedType: null }] };
  if (list.length > MAX_BARCODES) return { ok: false, barcodes: [], errors: [{ index: -1, value: '', message: `At most ${MAX_BARCODES} barcodes per variant.`, suggestedType: null }] };
  const errors = [];
  const seen = new Map();
  list.forEach((entry, index) => {
    const raw = entry && typeof entry === 'object' ? entry.value : entry;
    const type = entry && typeof entry === 'object' ? entry.type : null;
    if (type != null && type !== '' && !normalizeBarcodeType(type)) {
      errors.push({ index, value: String(raw || ''), message: `Unknown barcode type ${type}.`, suggestedType: suggestBarcodeType(raw) });
      return;
    }
    const [same] = normalizeBarcodeList([{ value: raw, type }]);
    const check = same && trustedKeys.has(`${same.value}|${same.type || ''}`) ? { valid: true } : validateBarcode(raw, type);
    if (!check.valid) { errors.push({ index, value: String(raw || ''), message: check.message, suggestedType: check.suggestedType }); return; }
    const key = barcodeMatchKey(normalizeBarcodeValue(raw));
    if (seen.has(key)) errors.push({ index, value: String(raw || ''), message: `${normalizeBarcodeValue(raw)} is listed twice.`, suggestedType: null });
    else seen.set(key, index);
  });
  return { ok: errors.length === 0, barcodes: errors.length ? [] : normalizeBarcodeList(list), errors };
}

/** Same set, same order, same types? (confirm a write by read-back). */
export function sameBarcodeList(a, b) {
  const left = normalizeBarcodeList(a);
  const right = normalizeBarcodeList(b);
  return left.length === right.length
    && left.every((entry, i) => entry.value === right[i].value && entry.type === right[i].type);
}

/**
 * A new primary (first) barcode in front of the rest, e.g. a scan into the
 * Products card's inline Barcode field on a variant that already has extra
 * barcodes. The SAME value keeps the list exactly as it is (type included:
 * every Products save re-sends the primary, and an untyped migrated barcode
 * must never get retyped by a bin or weight edit). A new value keeps the old
 * primary's type when it still fits, otherwise the type the value fits
 * (null = Other). Clearing the primary promotes the next.
 */
export function replacePrimaryBarcode(list, value) {
  const current = normalizeBarcodeList(list);
  const next = normalizeBarcodeValue(value);
  if (!next) return current.slice(1);
  const old = current[0];
  if (old && old.value === next) return current;
  const type = old?.type && barcodeFitsType(next, old.type) ? old.type : suggestBarcodeType(next);
  return normalizeBarcodeList([{ value: next, type }, ...current.slice(1)]);
}

/**
 * Apply an editor's change onto the variant's LIVE set (read from Shopify
 * just before the write). `base` is the list the editor opened with, `next`
 * what it saves. Removed = in base, not in next; retyped / reordered = in
 * both; added = only in next. Barcodes Shopify gained since the editor
 * opened (Shopify admin, another device) are kept, at the end; ones Shopify
 * lost are not brought back unless the editor added them. No base (an older
 * client) = `next` is the whole set, as sent.
 */
export function mergeBarcodeEdit(live, base, next) {
  const wanted = normalizeBarcodeList(next);
  if (!Array.isArray(base)) return wanted;
  const key = (entry) => barcodeMatchKey(entry.value);
  const liveList = normalizeBarcodeList(live);
  const baseKeys = new Set(normalizeBarcodeList(base).map(key));
  const liveKeys = new Set(liveList.map(key));
  const wantedKeys = new Set(wanted.map(key));
  // In the editor's order: everything it still wants that Shopify has, plus
  // what it added; skip what Shopify dropped behind the editor's back.
  const kept = wanted.filter((entry) => liveKeys.has(key(entry)) || !baseKeys.has(key(entry)));
  // Shopify-side additions the editor never saw (not in base, not removed by it).
  const extra = liveList.filter((entry) => !wantedKeys.has(key(entry)) && !baseKeys.has(key(entry)));
  // Over the cap stays over the cap, so validateBarcodeList refuses it ("At most 20")
  // instead of normalizeBarcodeList quietly dropping Shopify-side extras.
  const merged = [...kept, ...extra];
  return merged.length > MAX_BARCODES ? merged : normalizeBarcodeList(merged);
}

/**
 * A list the deprecated single `barcode` field can carry without loss: at
 * most one barcode and no type. Those keep the proven REST save path.
 */
export function isLegacyBarcodeList(list) {
  const clean = normalizeBarcodeList(list);
  return clean.length <= 1 && clean.every((entry) => !entry.type);
}

/**
 * The set a save should treat as current: the cached list when its first
 * barcode still equals the live one (liveFirst, from a REST variant read);
 * otherwise just the live first barcode. A stale cache must never make an
 * unchanged-looking value skip its write or resurrect old extras.
 * liveFirst undefined = no live read: the cached list as-is (null if none).
 */
export function currentBarcodesForWrite(doc, liveFirst) {
  const list = productBarcodeList(doc);
  if (liveFirst === undefined) return doc ? list : null;
  const live = normalizeBarcodeValue(liveFirst);
  if ((list[0]?.value || '') === live) return list;
  return live ? [{ value: live, type: null }] : [];
}

